import { createSelector } from 'reselect';
import appSelector from 'selectors/appSelector';
import providerSelector from 'selectors/providerSelector';
import { userAuthSelector } from 'selectors/authenticationSelectors';
import pageSelector from 'selectors/pageSelector';
import selectedContentSelector from 'selectors/selectedContentSelector';
import playingContentSelector from 'selectors/playingContentSelector';
import fanSelector from 'selectors/fanSelector';
import { getAccessMethodFromAuthTypes } from 'lib/auth';
import { OMNITURE_CONSTANTS, getOmniturePageName } from 'lib/omniture';
import cookie from 'cookie-cutter';

const Site = OMNITURE_CONSTANTS.Site;
const ReportSuite = OMNITURE_CONSTANTS.ReportSuite;
const TrackingServer = OMNITURE_CONSTANTS.TrackingServer;

// utility functions to format data specifically for nielsen
function yesOrNo(boolParam) {
    return (boolParam === true ? 'yes' : 'no');
}

function getNielsenValue(value) {
    return value || 'Not Applicable';
}

function getEventValueFromContent(content, value) {
    return (content.type === 'event' ? getNielsenValue(value) : 'Not Applicable');
}

function getNielsenVideoTypeFromContent(content = {}) {
    if (content.status) {
        // if the status of the event is over, it's linear, and the previous event was selected, so actually live
        if (content.status === 'over') {
            return 'live';
        }
        return content.status;
    }
    return 'vod';
}

function getNielsenPersonalizationData(fan) {
    if (Object.keys(fan).length > 0) {
        const { profile: { settings = {} } } = fan;
        const { activeProducts = {} } = settings;
        const { metaData = {} } = activeProducts;

        const hasFantasy = Object.keys(metaData).filter((key) => (metaData[key] === 'true'))
                .map((key) => (key.indexOf('lm_') !== -1)).length > 0;

        const hasFavorites = fan.preferences && (fan.preferences.length > 0);
        const hasNotifications = fan.profile && fan.profile.hasNotificationPreferences;
        const videoAutoStart = settings.videoAutoStart && (settings.videoAutoStart.value === '2');
        return `has favorites:${yesOrNo(hasFavorites)}-fantasy:${yesOrNo(hasFantasy)}-notifications:${hasNotifications}-docking:no-autostart:${yesOrNo(videoAutoStart)}`; // eslint-disable-line
    }
    return 'Not Applicable';
}

function getNielsenLoginStatusFromFan(fan) {
    if (Object.keys(fan).length > 0) {
        return 'Registered:Logged in Active';
    }
    return 'Not Applicable';
}

function formatNielsenData(
    fan = {},
    page = {},
    playingContent = {},
    provider = {},
    selectedContent = {},
    userAuth = []
) {
    const tracking = selectedContent.tracking || {};

    const Language = (page.lang || '').toLowerCase();
    const Placement = selectedContent.placement || 'player-direct';
    const PlayLocation = `watchespn:${getNielsenValue(getOmniturePageName(page))}`;
    const InsiderStatus = yesOrNo(fan.isInsider);

    // watch specific values
    const Network = getEventValueFromContent(selectedContent, selectedContent.sourceId);
    const AirDate = getEventValueFromContent(selectedContent, selectedContent.date);
    const AirTime = getEventValueFromContent(selectedContent, selectedContent.time);
    const AiringID = getEventValueFromContent(selectedContent, selectedContent.airings && selectedContent.airings[0].airingId);
    const ProgramCode = getEventValueFromContent(selectedContent, tracking.programCode);
    const SegC = ProgramCode;
    const SportCode = getEventValueFromContent(selectedContent, tracking.sportCode);


    const AccessMethod = getEventValueFromContent(selectedContent, getAccessMethodFromAuthTypes(userAuth));
    const AffiliateName = getEventValueFromContent(selectedContent, provider.name);
    const AffiliateID = getEventValueFromContent(selectedContent, provider.abbreviation);

    const LoginStatus = getNielsenLoginStatusFromFan(fan);
    const Personalization = getNielsenPersonalizationData(fan);
    const Embedded = 'no';

    const SWID = fan.id || (typeof document !== 'undefined' && cookie.get('SWID'));
    const unid = fan.id || (typeof document !== 'undefined' && cookie.get('UNID'));
    const UserAgent = (typeof window !== 'undefined') ? window.navigator.userAgent : 'Not Applicable';
    // #TODO get real orientation when mobile player launches
    const Orientation = 'Desktop';
    const VideoID = getNielsenValue(selectedContent.id);
    const VideoName = getNielsenValue(selectedContent.name);
    const VideoType = getNielsenVideoTypeFromContent(selectedContent);
    const ContentType = VideoType;
    const ContentDuration = tracking.duration || -1;
    const Sport = getNielsenValue(tracking.sport);
    let League = getNielsenValue(tracking.league);
    // if No League, set it equal to the sport, ask Hopkins.
    League = (League !== 'No League' ? League : Sport);

    const GameID = getNielsenValue(selectedContent.gameId);
    const PlayerName = (VideoType === 'vod') ? 'Brightcove-vod' : 'Brightcove-live';
    const VideoTypeDetail = getNielsenValue(tracking.coverageType);
    // #TODO: get these values
    const dateTimeArray = (selectedContent.type === 'video') ? (tracking.originalPublishDate || '').split('T') : [];
    const PublishDate = (selectedContent.type === 'video') ? dateTimeArray[0] : 'Not Applicable';
    const PublishTime = (selectedContent.type === 'video') ? dateTimeArray[1] : 'Not Applicable';
    const ExpirationDate = (selectedContent.type === 'video') ? tracking.expirationDate : 'Not Applicable';
    const DateLastModified = (selectedContent.type === 'video') ? tracking.lastModified : 'Not Applicable';
    const CMSID = (selectedContent.type === 'video') ? `${selectedContent.id}-${tracking.name}` : 'Not Applicable';
    const ShowCode = (selectedContent.type === 'video') ? tracking.showCode : 'Not Applicable';
    const VideoPlaylist = 'Not Applicable';
    const isfullepisode = (selectedContent.type === 'video') ? 'n' : 'y';

    // #TODO: StoryID will always be 'Not Applicable' until we identify where this value comes from
    const StoryID = 'Not Applicable';

    return {
        Site,
        ReportSuite,
        TrackingServer,
        SWID,
        UserAgent,
        Language,
        Placement,
        PlayLocation,
        Network,
        InsiderStatus,
        AirDate,
        AirTime,
        AiringID,
        SegC,
        ProgramCode,
        SportCode,
        LoginStatus,
        Personalization,
        Embedded,
        Orientation,
        AccessMethod,
        AffiliateID,
        AffiliateName,
        GameID,
        StoryID,
        VideoID,
        VideoName,
        VideoType,
        ContentType,
        ContentDuration,
        Sport,
        League,
        PlayerName,
        VideoTypeDetail,
        PublishDate,
        PublishTime,
        ExpirationDate,
        DateLastModified,
        CMSID,
        ShowCode,
        VideoPlaylist,
        isfullepisode,
        unid
    };
}

const analyticsSelector = ({ app: { analytics = null } = {} }) => analytics;

// last argument on the selector gets passed the previous arguments
export const nielsenTrackingSelector = createSelector(
    fanSelector,
    pageSelector,
    playingContentSelector,
    providerSelector,
    selectedContentSelector,
    userAuthSelector,
    formatNielsenData
);

export const pageTrackingSelector = createSelector(
    analyticsSelector,
    appSelector,
    pageSelector,
    providerSelector,
    userAuthSelector,
    (analytics, app, page, provider, userAuth) => ({
        analytics,
        app,
        page,
        provider,
        userAuth
    })
);

export const placementPrefixSelector = createSelector(
    pageSelector,
    (page) => {
        let prefix = page.id;
        if (page.id === 'collection') {
            prefix += `-${page.name}`;
        }
        prefix += '-';
        return prefix;
    }
);



// WEBPACK FOOTER //
// ./app/selectors/analyticsSelectors.js